"use client";

import { env } from "@/env.mjs";
import type { PaymentResult } from "@frend-digital/centra-types/orders";
import Script from "next/script";
import { useEffect } from "react";

const VOLUMENTAL_SDK_SRC = "https://js.volumental.com/sdk/v1/volumental.js";

interface VolumentalOrder {
	order_id: string;
	currency: string;
	country: string;
	order_value: number;
	order_lines: Array<{
		product_id: string;
		quantity: number;
		price: number;
	}>;
	subject?: string;
}

declare global {
	interface Window {
		// Assigning this triggers order registration; the SDK picks it up whenever it loads.
		VolumentalOrder?: VolumentalOrder;
	}
}

const buildOrder = (order: PaymentResult["order"]): VolumentalOrder | null => {
	if (!order?.order || !order.currency || !order.shippingAddress?.country) return null;

	const orderLines =
		order.items
			?.map((item) => ({
				product_id: item.product?.productSku || item.sku || "",
				quantity: item.quantity ?? 1,
				price: item.priceEachAsNumber ?? 0,
			}))
			.filter((line) => line.product_id) ?? [];

	if (!orderLines.length) return null;

	return {
		order_id: String(order.order),
		currency: order.currency,
		country: order.shippingAddress.country,
		order_value: order.totals?.grandTotalPriceAsNumber ?? 0,
		order_lines: orderLines,
	};
};

export const VolumentalOrderCapture = ({ order }: { order: PaymentResult["order"] }) => {
	const clientId = env.NEXT_PUBLIC_VOLUMENTAL_CLIENT_ID;

	useEffect(() => {
		if (!clientId || !order?.order) return;

		const sentKey = `volumental_order_sent_${order.order}`;
		if (sessionStorage.getItem(sentKey)) return;

		const payload = buildOrder(order);
		if (!payload) return;

		window.VolumentalOrder = payload;
		sessionStorage.setItem(sentKey, "true");
	}, [clientId, order]);

	if (!clientId) return null;

	return <Script src={VOLUMENTAL_SDK_SRC} strategy="afterInteractive" data-client-id={clientId} />;
};
